import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import type { MenuProps } from "antd";
import _findIndex from "lodash/findIndex";
import { RootState } from "store/rootReducer";

import { EntityKind } from "models/entityKind";

import { doesTileMatchFocus } from "components/groupBy/helpers";
import { useUserDefaults } from "components/user/hooks";

const useRecentFocusItems = (entityKind: EntityKind) => {
  // state
  const [list, setList] = useState<MenuProps["items"]>([
    {
      key: "-1",
      label: "No recent items."
    }
  ]);

  const defaults = useUserDefaults();
  const presetFields =
    (entityKind === EntityKind.Well
      ? defaults.focusTiles?.tiles
      : defaults.facilityFocusTiles?.tiles) ?? [];

  // state from store
  const groupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);
  const facilityGroupBy = useSelector(
    (state: RootState) => state.groupBy.globalFacilityFocus
  );
  const recentFocus = useSelector((state: RootState) =>
    entityKind === EntityKind.Well
      ? state.groupBy.recentWellFocus
      : state.groupBy.recentFacilityFocus
  );

  // derived state
  // The tile panel lights whichever tile matches the focus, and this decides whether "More
  // Fields" claims the selection instead, so both have to read a tile the same way. The
  // committed property no longer holds the date range a tile can carry, so comparing the two
  // strings as they are would let both controls claim the selection at once.
  const focus = entityKind === EntityKind.Well ? groupBy : facilityGroupBy;
  const isPresetFieldSelected =
    _findIndex(presetFields, (p) => doesTileMatchFocus(p, focus)) < 0;

  // update list
  useEffect(() => {
    if (!recentFocus.length) return;

    const nextList = recentFocus.map((preset) => ({
      key: JSON.stringify({
        property: preset.property,
        dataType: preset.dataType,
        isForecastToggleOn: preset.isForecastToggleOn,
        selectedForecastFolderName: preset.forecastFolder,
        partialContextTitle: preset.partialContextTitle,
        fullContextTitleWithNormalization: preset.fullContextTitleWithNormalization,
        pdenSource: preset.pdenSource
      }),
      label: preset.title
    })) as MenuProps["items"];

    setList(nextList);
  }, [recentFocus]);

  return { recentFocusItems: list, isPresetFieldSelected };
};

export default useRecentFocusItems;
